/**
 * Copyright 2022 Beijing Volcano Engine Technology Co., Ltd. All Rights Reserved.
 * SPDX-license-identifier: BSD-3-Clause
 */

import { AudioProfileType } from '@volcengine/rtc';
import 通用女声 from '@/assets/img/tongyongnvsheng.jpeg';
import 通用男声 from '@/assets/img/tongyongnansheng.jpeg';

export enum ModelSourceType {
  Custom = 'Custom',
  Available = 'Available',
}

/**
 * @brief 音频配置
 */
export const AudioProfile = [
  {
    text: '24KBps',
    type: AudioProfileType.default,
  },
  {
    text: '48KBps',
    type: AudioProfileType.standard,
  },
  {
    text: '128KBps',
    type: AudioProfileType.hd,
  },
];

/**
 * @brief AI 音色可选值
 * @default 活泼女声
 */
export enum VOICE_TYPE {
  '通用女声' = 'BV001_streaming',
  '通用男声' = 'BV002_streaming',
  '炀炀' = 'BV705_streaming',
  // ... 可根据所开通的音色进行扩充
}

export const VOICE_INFO_MAP = {
  [VOICE_TYPE['通用女声']]: {
    description: '女声 青年 语音合成 通用场景',
    url: '',
    icon: 通用女声,
  },
  [VOICE_TYPE['通用男声']]: {
    description: '男声 青年 语音合成 通用场景',
    url: '',
    icon: 通用男声,
  },
  [VOICE_TYPE['炀炀']]: {
    description: '男声 青年 语音合成 通用场景',
    url: '',
    icon: 通用男声,
  },
};

/**
 * @brief TTS 的 Cluster
 */
export enum TTS_CLUSTER {
  TTS = 'volcano_tts',
  MEGA = 'volcano_mega',
  ICL = 'volcano_icl',
}

/**
 * @brief 模型 Prompt
 */
export enum PROMPT {
  ARK_V3 = '## 角色\n' +
    '你是一位身着长袍、气质儒雅，并且精通儒家哲学的资深哲学咨询师，在与来访者交流时，总是面带微笑，眼神中透露出睿智与平和。你对孔子思想有深刻理解，能够自如地将孔子哲学精髓与现实生活紧密结合，为来访者在思想和生活中的困惑提供精准、独到且具启发性的见解。你善于倾听，敏锐洞察来访者的迷茫，以孔子哲学的丰富内涵引导其深入思考，成为值得信赖的哲学交流伙伴。你的目标是理解来访者，引导来访者学会自主思考，而非给出问题的答案。\n' +
    '\n' +
    '## 技能\n' +
    '### 技能 1：哲学咨询形式对话\n' +
    '1. 和你交流的是哲学咨询的来访者。在咨询过程中，与来访者平等交流、静心倾听，以开放、尊重的态度鼓励来访者充分表达，积极引导对话和深入研讨。\n' +
    '2. 深入剖析问题，精准洞察问题核心，如同一位技艺精湛的工匠梳理问题的结构与脉络。在回答时可以进行反问来确认来访者的问题以澄清相关概念或者用于更深度的剖析。\n' +
    '3. 可适时邀请来访者回答特定问题，细致分析答案，进行论证，明确内容和预设，洞察其局限性。\n' +
    '4. 回答的总字数应控制在250字以内，不超过150字为佳，保持语句连贯流畅，不使用分点形式。\n' +
    '5. 回答侧重于引导咨询者自主探索。\n' +
    '\n' +
    '### 技能 2：专业启迪\n' +
    '1. 回答可以援引一些儒家经典哲学理念和观点，夯实回答基础，但引用必须正确，且与回答上下文的意思准确匹配。\n' +
    '2. 运用尽可能浅易通俗的日常用语进行阐释，对于文言文等引据部分，务必确保普通人能够轻松理解。\n' +
    '3. 正确的使用标点符号。\n' +
    '\n' +
    '### 技能 3：跨文化交流\n' +
    '能按提问者的语言回答，向不同文化背景的人解释儒家哲学概念。仅当来访者的问题为英文时，使用英文进行回答。\n' +
    '\n' +
    '## 限制\n' +
    '- 仅围绕哲学咨询相关问题进行交流。\n' +
    '- 价值中立。保持开放心态，不将任何思想包括主流价值观强加给来访者，仅作为可能的探讨方向。\n' +
    '- 控制回答字数，务必清晰易懂、简洁明了，将所有回答集中于一段中。\n' +
    '- 在相邻的两轮对话中，一般不引用相同的论语句子。\n' +
    '- 严格按照既定格式和要求，保持友善的语气。不要要求来访者“你应该...，或者 你要...”, 而是说“你可以...”',
}

/**
 * @brief 模型可选值
 * @default DOUBAO_LITE_4K
 */
export enum AI_MODEL {
  DOUBAO_PRO_256K = 'doubao-pro-256k',
  // ... 可根据所开通的模型进行扩充
}

/**
 * @brief 模型来源
 */
export enum AI_MODEL_MODE {
  CUSTOM = 'CustomLLM',
  ARK_V3 = 'ArkV3',
}

/**
 * @brief 各模型对应的 Prompt
 */
export const AI_MODE_PROMPT = {
  [AI_MODEL.DOUBAO_PRO_256K]: PROMPT.ARK_V3,
};

/**
 * @brief 豆包模型的 ID
 */
export const ARK_V3_MODEL_ID: Record<AI_MODEL, string> = {
  //[AI_MODEL.DOUBAO_PRO_256K]: 'ep-20250208155640-b6w4l',
  [AI_MODEL.DOUBAO_PRO_256K]: 'ep-20260415235401-kp27w',
  // ... 可根据所开通的模型进行扩充
};
